import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  tap,
} from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { ICON_PATH } from '@app/utils/constants/icon-map.constants';
import { CONTENT_VIEW_MODAL_CONFIGS } from '@app/utils/constants/modal.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { Content, UpdateContentBlockPayload } from '@app/utils/interfaces/content.interface';
import { ContentGlobalEditorData, EditorBodyAdapter } from '@app/utils/interfaces/editor-interface';
import { OfferSectionData } from '@app/utils/interfaces/offer.interface';
import {
  TeaserSectionData,
  UpdateTeaserSectionPayload,
} from '@app/utils/interfaces/teaser.interface';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { ContentService } from '../../../services/content.service';
import { EditModalComponent } from '../edit-modal/edit-modal.component';
import { LargeModalComponent } from '../large-modal/large-modal.component';
import { OffersEditorBodyComponent } from '../offers-editor-body/offers-editor-body.component';
import { TeaserEditorBodyComponent } from '../teaser-editor-body/teaser-editor-body.component';
import { CategoriesEditorBodyComponent } from '../categories-editor-body/categories-editor-body.component';
import { CategoriesSectionData } from '@app/utils/interfaces/categories-interface';
import { Category } from '@app/utils/interfaces/categories.interface';

type GlobalEditorAdapter =
  | EditorBodyAdapter<UpdateContentBlockPayload>
  | EditorBodyAdapter<UpdateTeaserSectionPayload>
  | EditorBodyAdapter<OfferSectionData>
  | EditorBodyAdapter<CategoriesEditorBodyComponent>;

@Component({
  selector: 'app-content-global-editor',
  standalone: true,
  imports: [
    TranslateModule,
    MatDialogModule,
    MatButtonModule,
    MatSlideToggleModule,
    EditModalComponent,
    TeaserEditorBodyComponent,
    OffersEditorBodyComponent,
    CategoriesEditorBodyComponent,
  ],
  templateUrl: './content-global-editor.component.html',
  styleUrl: './content-global-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentGlobalEditorComponent implements OnInit {
  @ViewChild(EditModalComponent)
  private set contentEditor(value: EditModalComponent | undefined) {
    this.contentEditorRef.set(value);
    this.cdr.markForCheck();
  }

  @ViewChild(TeaserEditorBodyComponent)
  private set teaserEditor(value: TeaserEditorBodyComponent | undefined) {
    this.teaserEditorRef.set(value);
    this.cdr.markForCheck();
  }

  @ViewChild(OffersEditorBodyComponent)
  private set offersEditor(value: OffersEditorBodyComponent | undefined) {
    this.offersEditorRef.set(value);
    this.cdr.markForCheck();
  }

  @ViewChild(CategoriesEditorBodyComponent)
  private set categoriesEditor(value: CategoriesEditorBodyComponent | undefined) {
    this.categoriesEditorRef.set(value);
    this.cdr.markForCheck();
  }

  protected readonly data = inject<ContentGlobalEditorData>(MAT_DIALOG_DATA);
  protected readonly isPreviewMode = signal(false);
  protected readonly isSaving = signal(false);

  public readonly IconName = IconName;
  public readonly icons = ICON_PATH;
  public searchedCategories = signal<Category[]>([]);

  private readonly dialogRef = inject(MatDialogRef<ContentGlobalEditorComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly contentService = inject(ContentService);
  private readonly userStateService = inject(UserStateService);
  private readonly snackbarService = inject(SnackbarService);

  private readonly contentEditorRef = signal<EditModalComponent | undefined>(undefined);
  private readonly teaserEditorRef = signal<TeaserEditorBodyComponent | undefined>(undefined);
  private readonly offersEditorRef = signal<OffersEditorBodyComponent | undefined>(undefined);
  private readonly categoriesEditorRef = signal<CategoriesEditorBodyComponent | undefined>(
    undefined,
  );

  private searchStream$ = new Subject<string>();

  private readonly editorAdapter = computed((): GlobalEditorAdapter | undefined => {
    switch (this.data.type) {
      case 'content':
        return this.contentEditorRef();

      case 'teaser':
        return this.teaserEditorRef();

      case 'offers':
        return this.offersEditorRef();

      case 'categories':
        return this.categoriesEditorRef();

      default:
        return undefined;
    }
  });

  ngOnInit() {
    if (this.data.type === 'categories') {
      this.loadAllCategories();

      this.searchStream$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((query) => {
            return this.contentService.searchCategories(query);
          }),
          tap((res) => {
            this.searchedCategories.set(res?.data?.items ?? []);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();

      this.searchStream$.next('');
    }
  }

  protected readonly titleKey = computed((): string => {
    return this.isPreviewMode()
      ? 'shop-settings.content.global-editor.title.preview'
      : 'shop-settings.content.global-editor.title.edit';
  });

  protected readonly titleParams = computed(() => ({
    section: this.data.sectionLabel,
  }));

  protected readonly visibilityValue = computed((): boolean => {
    return this.editorAdapter()?.getVisibilityValue() ?? false;
  });

  protected readonly canPreview = computed((): boolean => {
    return this.editorAdapter()?.canPreview() ?? false;
  });

  protected readonly contentPayload = this.data.payload as Content;
  protected readonly teaserPayload = this.data.payload as TeaserSectionData;
  protected readonly offersPayload = this.data.payload as OfferSectionData;
  protected readonly categoriesPayload = this.data.payload as CategoriesSectionData;

  protected allCategoriesData = signal<Category[]>([]);

  protected togglePreview(): void {
    const adapter = this.editorAdapter();

    if (!adapter?.canPreview()) {
      return;
    }

    if (this.data.type === 'content' && adapter.getPreviewContent) {
      const previewContent = adapter.getPreviewContent();

      this.dialogRef.close();
      this.dialog.open(LargeModalComponent, {
        ...CONTENT_VIEW_MODAL_CONFIGS,
        panelClass: ['no-radius-dialog', 'content-view-dialog'],
        data: previewContent,
      });
      return;
    }

    this.isPreviewMode.update((value: boolean) => !value);
  }

  protected onVisibilityToggle(event: MatSlideToggleChange): void {
    this.editorAdapter()?.setVisibilityValue(event.checked);
  }

  protected save(): void {
    const adapter = this.editorAdapter();
    if (!adapter) {
      return;
    }
    if (!adapter.isValid()) {
      adapter.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);

    switch (this.data.type) {
      case 'content':
        this.saveContent(adapter as EditorBodyAdapter<UpdateContentBlockPayload>);
        break;

      case 'teaser':
        this.saveTeaser(adapter as EditorBodyAdapter<UpdateTeaserSectionPayload>);
        break;

      case 'offers':
        this.saveOffers(adapter as EditorBodyAdapter<OfferSectionData>);
        break;

      case 'categories':
        this.saveCategories(adapter as EditorBodyAdapter<CategoriesSectionData>);
        break;

      default:
        this.isSaving.set(false);
    }
  }

  protected onCancel(): void {
    const adapter = this.editorAdapter();

    if (!adapter?.hasChanges()) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.addPanelClass('hidden-dialog');

    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      panelClass: 'confirm-dialog',
    });

    confirmRef
      .afterClosed()
      .pipe(
        tap((confirmed: boolean) => {
          if (confirmed) {
            this.dialogRef.close();
            return;
          }

          this.dialogRef.removePanelClass('hidden-dialog');
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private saveContent(adapter: EditorBodyAdapter<UpdateContentBlockPayload>): void {
    const userData = this.userStateService.userData() as {
      data?: { shopId?: string | number };
    } | null;

    const shopId = userData?.data?.shopId?.toString();

    if (!shopId) {
      this.isSaving.set(false);
      return;
    }

    const payload = adapter.getSubmitPayload();
    const content = this.data.payload as Content;

    this.contentService
      .updateContentBlock(shopId, content.apiSectionType, payload)
      .pipe(
        tap(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.SUCCESS);

          this.dialogRef.close({
            type: 'content',
            payload: {
              ...content,
              ...payload,
            },
          });
        }),
        catchError(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.FAIL);
          this.dialogRef.close(false);

          return EMPTY;
        }),
        finalize(() => {
          this.isSaving.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private saveTeaser(adapter: EditorBodyAdapter<UpdateTeaserSectionPayload>): void {
    const payload = adapter.getSubmitPayload();
    const currentTeaser = this.data.payload as TeaserSectionData;

    this.contentService
      .updateTeaserSection(payload)
      .pipe(
        tap(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.SUCCESS);

          const teaserResultPayload: TeaserSectionData = {
            isVisible: payload.isVisible,
            heading: payload.heading,
            additionalText: payload.additionalText,
            link: payload.link,
            teaserImageUrl: currentTeaser.teaserImageUrl,
            headingAlignment: payload.headingAlignment,
            additionalTextAlignment: payload.additionalTextAlignment,
            headingColor: payload.headingColor,
            additionalTextColor: payload.additionalTextColor,
          };

          this.dialogRef.close({
            type: 'teaser',
            payload: teaserResultPayload,
          });
        }),
        catchError(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.FAIL);
          this.dialogRef.close(false);

          return EMPTY;
        }),
        finalize(() => {
          this.isSaving.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
  private saveOffers(adapter: EditorBodyAdapter<OfferSectionData>): void {
    const payload = adapter.getSubmitPayload();

    this.contentService
      .updateOfferSection(payload)
      .pipe(
        tap(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.SUCCESS);

          this.dialogRef.close({
            type: 'offers',
            payload,
          });
        }),
        catchError(() => {
          this.snackbarService.openSnackbar(SnackbarStatus.FAIL);
          this.dialogRef.close(false);

          return EMPTY;
        }),
        finalize(() => {
          this.isSaving.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private saveCategories(adapter: EditorBodyAdapter<CategoriesSectionData>): void {
    this.isSaving.set(false);
  }

  public loadAllCategories() {
    this.contentService
      .getAllCategories()
      .pipe(
        tap((res) => {
          this.allCategoriesData.set(res.data.categories);
        }),
        catchError(() => {
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public categoriesSearchChanges(query: string) {
    this.searchStream$.next(query);
  }
}
